import { useContext } from 'react'

import { launchModal, setQueryParamsToHash } from '@domain-group/javascript-utils'

import type { UseFormOptions } from '@domain-group/react-hooks'
import { useForm, useLatestEntity } from '@domain-group/react-hooks'

import useDispatch from '@hooks/useDispatch'
import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import PageContext from '@contexts/pageContext'

import { mergeAndStringifyEntityObjects } from '@functions/entity'
import { baseURL } from '@functions/api'

import * as publicationBookingActions from '@redux/modules/publicationBooking'

import { EntityOptions } from '@redux/modules/module'
import { AppDispatch } from '@redux/store'

import defaultFormState, { requiredFields } from '@models/publicationBooking'

import type { PublicationBookingModel, PublicationBookingRequestOptions } from '@models/types'

const stringifyArrayKeys: (keyof PublicationBookingModel)[] = ['page_options']
const stringifyObjectKeys: (keyof PublicationBookingModel)[] = ['ftp_transfer_data']

export const generateUrls = (publicationBooking?: PublicationBookingModel) => ({
  bookingsUrl: '#/newsDesk/bookings',
  domainXml: `${baseURL}/publication_bookings/${publicationBooking?.id}/domain_xml.json`,
  downloadLowResPDFUrl: `${baseURL}/publication_bookings/${publicationBooking?.id}/pdf?low_res=true`,
  downloadPDFUrl: `${baseURL}/publication_bookings/${publicationBooking?.id}/pdf`,
  preflightingUrl: '#/newsDesk/preflighting',
})

export type Contact = {
  email?: string,
  mobile_number: string,
  user_id: number,
  title?: string,
  full_name?: string,
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

type ApprovePublicationBookingParams = {
  dispatch: AppDispatch,
  publicationBooking: Partial<PublicationBookingModel>,
  requestOptions?: object,
}

const approvePublicationBooking = (params: ApprovePublicationBookingParams) => {
  const { dispatch, publicationBooking, requestOptions } = params
  const { approvePublicationBooking: approveFn } = publicationBookingActions

  // @ts-expect-error TS2345
  return dispatch(approveFn(publicationBooking, requestOptions))
}

type UnapprovePublicationBookingParams = {
  dispatch: AppDispatch,
  publicationBooking: Partial<PublicationBookingModel>,
  requestOptions?: object,
}

const unapprovePublicationBooking = (params: UnapprovePublicationBookingParams) => {
  const { dispatch, publicationBooking, requestOptions } = params
  const { unapprovePublicationBooking: unapproveFn } = publicationBookingActions

  // @ts-expect-error TS2345
  return dispatch(unapproveFn(publicationBooking, requestOptions))
}

type CreatePublicationBookingParams = {
  dispatch: AppDispatch,
  publicationBookingParams: Partial<PublicationBookingModel>,
  requestOptions?: object,
}

const createPublicationBooking = (params: CreatePublicationBookingParams) => {
  const { dispatch, publicationBookingParams, requestOptions } = params
  const { createPublicationBooking: createFn } = publicationBookingActions

  return dispatch(createFn(publicationBookingParams, requestOptions))
}

type CheckPublicationBookingArtworksParams = {
  dispatch: AppDispatch,
  publicationBooking: PublicationBookingModel,
  requestOptions?: object,
}

const checkPublicationBookingArtworks = (params: CheckPublicationBookingArtworksParams) => {
  const { dispatch, publicationBooking, requestOptions } = params
  const { checkPublicationBookingArtworks: checkFn } = publicationBookingActions

  return dispatch(checkFn(publicationBooking.id, requestOptions))
}

type UpdatePublicationBookingParams = {
  dispatch: AppDispatch,
  publicationBooking: PublicationBookingModel,
  publicationBookingParams: Partial<PublicationBookingModel>,
  requestOptions?: PublicationBookingRequestOptions,
}

const updatePublicationBooking = (params: UpdatePublicationBookingParams) => {
  const {
    dispatch, publicationBooking, publicationBookingParams, requestOptions,
  } = params
  const { updatePublicationBooking: updateFn } = publicationBookingActions

  const updatedParams = mergeAndStringifyEntityObjects(
    publicationBooking,
    publicationBookingParams,
    stringifyObjectKeys,
    stringifyArrayKeys,
  )

  return dispatch(updateFn(updatedParams, requestOptions))
}

type UpdatePreflightStatusParams = {
  dispatch: AppDispatch,
  publicationBooking: PublicationBookingModel,
  publicationBookingParams: Partial<PublicationBookingModel>,
  requestOptions?: EntityOptions,
}

const updatePublicationBookingPreflightStatus = (params: UpdatePreflightStatusParams) => {
  const {
    publicationBooking, publicationBookingParams, dispatch, requestOptions,
  } = params
  const { updatePublicationBookingPreflightStatus: updateFn } = publicationBookingActions

  const updatedParams = mergeAndStringifyEntityObjects(
    publicationBooking,
    publicationBookingParams,
    stringifyObjectKeys,
    stringifyArrayKeys,
  )

  return dispatch(updateFn(updatedParams, requestOptions))
}

const getBookingIndex = (selected: Partial<PublicationBookingModel>, bookings: PublicationBookingModel[]) => (
  selected.id ? bookings.findIndex(booking => booking.id === selected.id) : 0
)

type deletePublicationBookingParams = {
  dispatch: AppDispatch,
  publicationBooking: Partial<PublicationBookingModel>,
  filteredPublicationBookings: PublicationBookingModel[],
}

const deletePublicationBooking = (params: deletePublicationBookingParams) => {
  const {
    dispatch,
    filteredPublicationBookings: publicationBookings,
    publicationBooking,
  } = params

  const { deletePublicationBooking: deleteFn } = publicationBookingActions

  return dispatch(deleteFn(publicationBooking)).then((response) => {
    const { success } = response

    const index = getBookingIndex(publicationBooking, publicationBookings)

    const prevBooking = publicationBookings[index - 1] || null
    const nextBooking = publicationBookings[index + 1] || null

    if (success){
      const entityParams = {
        publicationId: publicationBooking.publication_id,
        publicationEditionId: publicationBooking.publication_edition_id,
        publicationBookingId: prevBooking?.id || nextBooking?.id,
      }

      setQueryParamsToHash(entityParams)

      return response
    }

    return response
  })
}

type ftpSyncParams = {
  dispatch: AppDispatch,
  publicationBooking: PublicationBookingModel,
}

const ftpSync = (params: ftpSyncParams) => {
  const { dispatch, publicationBooking } = params
  const { ftpSync: syncFn } = publicationBookingActions

  return dispatch(syncFn(publicationBooking))
}

export type SendUnapprovedArtworkMessagePayload = {
  contacts: Contact[],
  publicationBookingId: number,
  message: string,
}

type SendUnapprovedArtworkMessageParams = {
  dispatch: AppDispatch,
  sendMessagePayload: SendUnapprovedArtworkMessagePayload,
}

const sendUnapprovedArtworkMessage = (params: SendUnapprovedArtworkMessageParams) => {
  const { sendMessagePayload, dispatch } = params
  const { sendUnapprovedArtworkMessage: sendMessageFn } = publicationBookingActions

  return dispatch(sendMessageFn(sendMessagePayload))
}

export function usePublicationBookingForm(
  publicationBooking: Partial<PublicationBookingModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const publicationBookingForm = useForm(
    defaultFormState,
    { entity: publicationBooking, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [publicationBooking.id, publicationBooking.cache_key],
  )

  return {
    ...publicationBookingForm,
  }
}

export const useRelations = (publicationBooking: Partial<PublicationBookingModel> = {}) => {
  const {
    brand_group_id,
    layout_template_id,
    preflight_status_id,
    publication_edition_id,
    publication_id,
    publication_order_id,
    publication_product_id,
  } = publicationBooking

  const {
    brandGroups,
    layoutTemplates,
    publications,
    publicationEditions,
    publicationOrders,
    publicationProducts,
    statuses,
  } = useSelector(reduxState => reduxState.entities)

  const brandGroup = brand_group_id && brandGroups[brand_group_id] ? brandGroups[brand_group_id] : {}
  const layoutTemplate = layout_template_id && layoutTemplates[layout_template_id]
    ? layoutTemplates[layout_template_id] : {}
  // @ts-expect-error TS2538
  const publication = publications[publication_id] || {}
  const publicationEdition = publication_edition_id && publicationEditions[publication_edition_id]
    ? publicationEditions[publication_edition_id]
    : {}
  const publicationOrder = publication_order_id && publicationOrders[publication_order_id]
    ? publicationOrders[publication_order_id]
    : {}
  const publicationProduct = publication_product_id && publicationProducts[publication_product_id]
    ? publicationProducts[publication_product_id]
    : {}
  const preflightStatus = preflight_status_id && statuses[preflight_status_id] ? statuses[preflight_status_id] : {}

  return {
    brandGroup,
    layoutTemplate,
    preflightStatus,
    publication,
    publicationEdition,
    publicationOrder,
    publicationProduct,
  }
}

type Options = {
  performHttpRequests?: boolean,
  requestOptions?: PublicationBookingRequestOptions,
}

function usePublicationBooking(
  initEntity: Partial<PublicationBookingModel> = {},
  options: Options = {},
) {
  const { performHttpRequests = false, requestOptions } = options

  const latestEntity = useLatestEntity(initEntity, 'publicationBookings')
  // @ts-expect-error TS2322
  const { entity: publicationBooking }: { entity: PublicationBookingModel } = latestEntity

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)

  const {
    brandGroup,
    layoutTemplate,
    preflightStatus,
    publication,
    publicationEdition,
    publicationOrder,
    publicationProduct,
  } = useRelations(publicationBooking)

  const {
    creating, deleting, loading, updating,
  } = useSelector(reduxState => reduxState.publicationBookings)

  useReduxAction(
    'publicationBookings',
    'loadPublicationBooking',
    {
      entityId: publicationBooking.id,
      ...requestOptions,
    },
    [publicationBooking.id, performHttpRequests],
    {
      dispatchAction: (action, actionRequestOptions) => action(publicationBooking.id, actionRequestOptions),
      shouldPerformFn: () => !!performHttpRequests && !!publicationBooking.id,
    },
  )

  return {
    brandGroup,
    callbacks: {
      approvePublicationBooking: (entityOptions?: object) => (
        approvePublicationBooking({
          dispatch,
          publicationBooking,
          requestOptions: entityOptions,
        })
      ),
      checkPublicationBookingArtworks: (entityOptions?: object) => (
        checkPublicationBookingArtworks({
          dispatch,
          publicationBooking,
          requestOptions: entityOptions,
        })
      ),
      createPublicationBooking: (
        publicationBookingParams: Partial<PublicationBookingModel>,
        entityOptions?: object,
      ) => (
        createPublicationBooking({
          dispatch,
          publicationBookingParams,
          requestOptions: entityOptions,
        })
      ),
      deletePublicationBooking: (params: {
        filteredPublicationBookings: PublicationBookingModel[],
      }) => deletePublicationBooking({
        dispatch, publicationBooking, ...params,
      }),
      ftpSync: () => ftpSync({ dispatch, publicationBooking }),
      managePublicationBooking: () => launchModal({
        callbacks,
        modalKey: 'ManagePublicationBookingModal',
        payload: { publicationBooking },
      }),
      messageBookingContacts: () => launchModal({
        callbacks,
        modalKey: 'MessageBookingContactsModal',
        payload: { publicationBooking },
      }),
      sendUnapprovedArtworkMessage: (
        sendMessagePayload: SendUnapprovedArtworkMessagePayload,
      ) => sendUnapprovedArtworkMessage({ sendMessagePayload, dispatch }),
      unapprovePublicationBooking: (entityOptions?: object) => (
        unapprovePublicationBooking({
          dispatch,
          publicationBooking,
          requestOptions: entityOptions,
        })
      ),
      updatePublicationBooking: (
        publicationBookingParams: PublicationBookingModel,
        requestOptions?: PublicationBookingRequestOptions,
      ) => (
        updatePublicationBooking({
          publicationBooking, publicationBookingParams, dispatch, requestOptions,
        })
      ),
      updatePublicationBookingPreflightStatus: (
        publicationBookingParams: Partial<PublicationBookingModel>,
        requestOptions: EntityOptions,
      ) => (
        updatePublicationBookingPreflightStatus({
          publicationBooking, publicationBookingParams, dispatch, requestOptions,
        })
      ),
    },
    creating,
    deleting,
    layoutTemplate,
    loading,
    preflightStatus,
    publication,
    publicationBooking,
    publicationEdition,
    publicationOrder,
    publicationProduct,
    updating,
    urls: generateUrls(publicationBooking),
  }
}

export type UsePublicationBookingPayload = ReturnType<typeof usePublicationBooking>

export default usePublicationBooking
